/**
 * Merged config from Studio + Designer
 * Detects environment from hostname; local dev uses Vite proxy
 *
 * Worker URL mapping per ADR-006 (.kiro/governance/decisions/006-endpoint-integration-contract.md):
 *   catalog-api / images-api      → creator-api  (mms-creator: assets / cards / catalog / generate / vision)
 *   moderation-api                → admin-api    (mms-platform-api: kb-proposals + capability registry)
 *   rec-api                       → admin-api    (mms-platform-api: /api/recommendation/* + /api/track)
 *   supplier-sds-api              → admin-api    (mms-platform-api: SDS sync proxy)
 *   shopify-api (cart)            → customer-api (mms-commerce)
 *   customer-api                  → unchanged    (mms-commerce, still live)
 */

function detectEnvironment() {
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development'
  if (hostname.includes('.pages.dev')) return 'staging'
  return 'production'
}

const ENV = detectEnvironment()

// In local dev, Vite proxies all API calls — use relative paths
// In production, use full URLs to Cloudflare workers
const isLocal = ENV === 'development'

export const CONFIG = {
  ENVIRONMENT: ENV,

  // Studio APIs — creator catalog and image library.
  FRONTEND_API: isLocal ? '' : 'https://creator-api.mamamiya.store',
  IMAGES_API: isLocal ? '/images-api' : 'https://creator-api.mamamiya.store',
  // Generic creator-api base (H-090: short-links moved from customer-api).
  CREATOR_API: isLocal ? '/creator-api' : 'https://creator-api.mamamiya.store',
  CUSTOMER_API: isLocal ? '/customer-api' : 'https://customer-api.mamamiya.store',
  PICGUARD_API: isLocal ? '' : 'https://admin-api.mamamiya.store',

  // Designer APIs — supplier-sds-api proxied via admin-api;
  // Shopify publish/republish/delete now lives on mms-creator
  // (creator-api) per ADR-002 step 9.
  SDS_API: isLocal ? '/sds-api' : 'https://admin-api.mamamiya.store',
  SHOPIFY_API: isLocal ? '/creator-api' : 'https://creator-api.mamamiya.store',

  // Designer URL (for backward compat — may still be used by extension)
  DESIGNER_URL: isLocal ? 'http://localhost:5173' : 'https://creator.mamamiya.store',

  // CDN
  CDN_URL: 'https://cdn.mamamiya.store',

  // Image CDN — Cloudflare Images served through OUR zone domain via the
  // reserved edge path /cdn-cgi/imagedelivery/<account_hash> (no worker;
  // verified 200 image/jpeg 2026-06-03). The previous value pointed at
  // creator-api/cdn — a route that was never implemented (git -S confirms),
  // so every full-size gallery URL 404'd. Account hash is public (it is in
  // every delivered URL).
  IMAGE_CDN: 'https://cdn.mamamiya.store/cdn-cgi/imagedelivery/LsW-PcMLGW-BlYYP5LphtQ',

  // API hosts for creator catalog and customer services.

  // Limits
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  GALLERY_PAGE_SIZE: 20,

  // Feature flags (creator-dialog-v07)
  // Flip BUNDLE_CARDS_EDITABLE true once images worker ships PATCH/POST
  // /api/images/:id/cards (H-2026-04-25-010 @hephaestus).
  BUNDLE_CARDS_EDITABLE: false,
}

/**
 * Get optimized thumbnail URL for an image object.
 * Prefers CDN proxy with Cloudflare Images flexible variants when cloudflare_id is available.
 * Falls back to raw URL fields for non-Cloudflare images.
 */
export function getCdnThumbnail(img, width = 200) {
  // CF Images path ONLY when the row carries a real cloudflare_id. R2-backed
  // rows (H-139+) have no CF image — their DB row id is not a CF image id,
  // and building a variant URL from it returns text/plain + nosniff, which
  // Chrome ORB-blocks (blank Arts thumbnails, 2026-09-14 prod incident).
  if (img.cloudflare_id) return `${CONFIG.IMAGE_CDN}/${img.cloudflare_id}/w=${width},fit=scale-down,format=auto`
  return img.thumbnail_url || img.thumbnail || img.main_image_url || img.url
}

export default CONFIG
